import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { X, Download, Loader2, Printer } from 'lucide-react'

import { api } from '@/api/client'
import type { PayableDetailResponse } from '@/types/models'
import { formatNum } from '@/lib/utils'
import { useFileDownload } from '@/lib/useFileDownload'

interface Props {
  partnerId: number
  partnerName: string
  from: string
  to: string
  onClose: () => void
}

/** 채무 거래내역 — 거래처 1곳의 입고·지급·수금·미지급잔액(고객 요청 2026-10-15).
 *  수금은 참고 표시만(잔액 미반영). 인쇄 방식은 ReceivableDetailModal 과 같음. */
export function PayableDetailModal({ partnerId, partnerName, from, to, onClose }: Props) {
  const { downloading, download } = useFileDownload()
  const qs = `partner=${partnerId}&from=${from}&to=${to}`
  const { data, isLoading } = useQuery({
    queryKey: ['payable-detail', partnerId, from, to],
    queryFn: () => api<PayableDetailResponse>(`/api/ledger/payable/detail?${qs}`),
  })

  return createPortal(
    <div id="pd-detail-portal">
      <style>{`@media print {
        body > *:not(#pd-detail-portal) { display: none !important; }
        #pd-detail-portal, #pd-detail-portal * { visibility: visible !important; }
        #pd-detail-portal .pd-overlay {
          position: static !important; background: none !important;
          padding: 0 !important; display: block !important;
        }
        #pd-detail-portal .pd-card {
          max-height: none !important; max-width: none !important; width: 100% !important;
          box-shadow: none !important; border-radius: 0 !important;
        }
        #pd-detail-portal .pd-scroll { overflow: visible !important; max-height: none !important; }
        #pd-detail-portal .no-print { display: none !important; }
        #pd-detail-portal thead { display: table-header-group; }
        #pd-detail-portal thead th, #pd-detail-portal tfoot td { position: static !important; }
        #pd-detail-portal table { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        @page { size: A4 portrait; margin: 10mm; }
      }`}</style>

      <div className="pd-overlay fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
           onClick={onClose}>
        <div className="pd-card bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
             onClick={(e) => e.stopPropagation()}>
          <header className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-200">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-slate-900">거래내역 — {partnerName}</h3>
              <p className="text-sm text-slate-500">{from} ~ {to} · 입고·지급·수금 · 수금은 참고(미지급잔액에 미반영)</p>
            </div>
            <button onClick={() => window.print()} disabled={!data}
                    className="no-print flex items-center gap-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50 text-sm font-semibold rounded-lg px-3.5 py-2 disabled:opacity-40">
              <Printer className="w-4 h-4"/> 인쇄
            </button>
            <button onClick={() => download(`/api/ledger/payable/detail/export?${qs}`,
                                            `채무거래내역_${partnerName}_${from}_${to}.xlsx`)}
                    disabled={downloading || !data}
                    className="no-print flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg px-3.5 py-2 disabled:opacity-40">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}
              엑셀 다운로드
            </button>
            <button onClick={onClose} className="no-print text-slate-400 hover:text-slate-700 p-1">
              <X className="w-5 h-5"/>
            </button>
          </header>

          <div className="pd-scroll overflow-auto flex-1">
            {isLoading && (
              <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mr-2"/> 불러오는 중...
              </div>
            )}
            {data && (
              <table className="w-full text-sm tabular-nums">
                <thead className="sticky top-0 bg-slate-50 text-xs text-slate-500 z-10">
                  <tr className="border-b border-slate-200">
                    <th className="px-3 py-2 text-left font-medium w-28">날짜</th>
                    <th className="px-3 py-2 text-left font-medium w-32">구분</th>
                    <th className="px-3 py-2 text-right font-medium">입고금액</th>
                    <th className="px-3 py-2 text-right font-medium">지급액</th>
                    <th className="px-3 py-2 text-right font-medium">수금액</th>
                    <th className="px-3 py-2 text-right font-medium">미지급잔액</th>
                    <th className="px-3 py-2 text-left font-medium">적요</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100 bg-amber-50/50">
                    <td className="px-3 py-1.5 font-medium text-slate-600" colSpan={5}>전기(월)이월</td>
                    <td className="px-3 py-1.5 text-right font-semibold">{formatNum(data.opening)}</td>
                    <td></td>
                  </tr>
                  {data.rows.map((r, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-3 py-1.5">{r.date}</td>
                      <td className="px-3 py-1.5">{r.label}</td>
                      <td className="px-3 py-1.5 text-right">{r.kind === 'receiving' ? formatNum(r.amount) : ''}</td>
                      <td className="px-3 py-1.5 text-right text-purple-700">{r.kind === 'payment' ? formatNum(r.amount) : ''}</td>
                      <td className="px-3 py-1.5 text-right text-blue-700">{r.kind === 'receipt' ? formatNum(r.amount) : ''}</td>
                      <td className="px-3 py-1.5 text-right font-semibold">{formatNum(r.balance)}</td>
                      <td className="px-3 py-1.5 text-slate-500 text-xs">{r.note}</td>
                    </tr>
                  ))}
                  {!data.rows.length && (
                    <tr><td colSpan={7} className="text-center text-slate-400 py-10">이 기간에 거래내역이 없습니다.</td></tr>
                  )}
                </tbody>
                <tfoot className="sticky bottom-0 bg-slate-100 font-bold">
                  <tr>
                    <td className="px-3 py-2" colSpan={2}>합계</td>
                    <td className="px-3 py-2 text-right">{formatNum(data.totals.purchases)}</td>
                    <td className="px-3 py-2 text-right text-purple-700">{formatNum(data.totals.paid)}</td>
                    <td className="px-3 py-2 text-right text-blue-700">{formatNum(data.totals.received)}</td>
                    <td className="px-3 py-2 text-right text-purple-700">{formatNum(data.totals.closing)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
